import React, { useState } from "react";

import "./dashboard_page.less";
import {
  Headline,
  SimpleCustomCard,
  FilterSelection,
  FilterCollapse,
} from "@src/components";
import { COLORS, REQUEST_STATUS } from "@src/constants";
import {
  RequestCountryStatisticsChart,
  RequestRepliedChart,
} from "@src/features/dashboard/components";
import {
  getTimeRangeOptions,
  mapToActiveFilter,
  mapToFilterPanel,
} from "@src/features/dashboard/utils/dashboard_utils";
import { RequestStatusStatisticsChart } from "@src/features/request_status_statistics_chart/components/request_status_statistics_chart/index";
import { ActiveFilterBar } from "@src/features/table_filtering/components/active_filter_bar";
import { TotalFteChart } from "@src/features/total_fte_chart/components/total_fte_chart/index";
import { useGetManagersDashboardStatisticsQuery } from "@src/services/apis/managersApi";
import { DashboardFilterPayload, Employee, FilterPanel } from "@src/types";
import { RequestStatisticsSettings } from "@src/types/dashboard_types";
import { Col, Layout, Row } from "antd";
import _ from "lodash";
import Cookies from "universal-cookie";

const DashBoardPage = () => {
  const cookies = new Cookies();
  const empObj: Employee = cookies.get("loggedInuser");

  const [selectedCollapseFilter, setSelectedCollapseFilter] = useState<
    Map<string, string[]>
  >(new Map());
  const [timeRangeFilter, setTimeRangeFilter] = useState<number>(6);

  const dashboardFilterPayload: DashboardFilterPayload = {
    managerId: empObj.employeeId,
    monthRange: timeRangeFilter,
    ...Object.fromEntries(selectedCollapseFilter),
  };

  const { data: dashboardStatistics, currentData } =
    useGetManagersDashboardStatisticsQuery(dashboardFilterPayload);

  const filterCollapseData: FilterPanel[] = mapToFilterPanel(
    dashboardStatistics?.requests,
    selectedCollapseFilter
  );

  /**
   * Filters by the given time range filter. The time range filter filters the requestStartDate.
   * That means if a request start date is before the current date than the requests will also not be visible.
   * @param selectedTimeRange The selected time range in months (e.g. 3, 6, 12)
   */
  function onFilterTimeRange(selectedTimeRange: number) {
    setTimeRangeFilter(selectedTimeRange);
  }

  /**
   * Filters the dashboard statistics data by the given checkbox filter and will update the state so that the charts will be rendered again.
   * @param {string} filterType The name of the current selected filters (e.g. "Regions")
   * @param {string[]} allCheckedValues The current selected elements from the checkbox list (e.g. ["APAC", "Germany"])
   */
  function onFilterCollapse(filterType: string, allCheckedValues: string[]) {
    setSelectedCollapseFilter((currentFilter: Map<string, string[]>) => {
      if (allCheckedValues.length < 1) {
        currentFilter.delete(filterType);
        return new Map(currentFilter);
      }

      return new Map(currentFilter).set(filterType, allCheckedValues);
    });
  }

  /**
   *  Will give the current data update status. It will check the dashboard statistics with current data to see if
   *  there are changes or not. Maybe there is also a better alternative but there were problems
   *  with isLoading and isFetching from rtk that`s why this was implemented
   *  @return The current updated status as boolean
   */
  function isLoading(): boolean {
    return !dashboardStatistics || dashboardStatistics !== currentData;
  }

  /**
   * Triggered when a filter gets removed in the active filter section
   * onClose function of an active filter tag
   * @param filterType The name of the current selected filters (e.g. "Regions")
   * @param filterValueToBeRemoved The filter value that is clicked for removing
   */
  function onRemoveActiveFilter(
    filterType: string,
    filterValueToBeRemoved: string
  ) {
    const filterValues: string[] = selectedCollapseFilter.get(filterType);

    const newSelectedFilterValues: string[] = filterValues.filter(
      (value: string) => value !== filterValueToBeRemoved
    );

    onFilterCollapse(filterType, newSelectedFilterValues);
  }

  /** Resets all filter */
  function deleteAllFilters() {
    setSelectedCollapseFilter(new Map());
  }

  /**
   * Gets the settings for the request status statistics chart.
   * @returns The settings for the request status statistics chart.
   */
  function getRequestStatusChartSettings(): RequestStatisticsSettings {
    const colors: string[] = [
      COLORS.BRAND_60,
      COLORS.ENERGY_BLUE_40,
      COLORS.SUCCESS,
      COLORS.YELLOW_30,
      COLORS.DARK_RED_50,
    ];

    const labels: string[] = [
      REQUEST_STATUS.REQUESTED,
      REQUEST_STATUS.PROPOSED,
      REQUEST_STATUS.CONFIRMED,
      _.startCase(REQUEST_STATUS.IN_RECRUITMENT),
      REQUEST_STATUS.DECLINED,
    ];

    const data: number[] = [
      dashboardStatistics?.requestStatusStatistics?.requested,
      dashboardStatistics?.requestStatusStatistics?.proposed,
      dashboardStatistics?.requestStatusStatistics?.confirmed,
      dashboardStatistics?.requestStatusStatistics?.inRecruitment,
      dashboardStatistics?.requestStatusStatistics?.declined,
    ];

    return {
      labels: labels,
      colors: colors,
      data: data,
      total: dashboardStatistics?.requestStatusStatistics?.total,
    };
  }

  return (
    <Layout className="management-dashboard">
      <div className="management-dashboard__content">
        <Headline title="Dashboard" />
        <Row gutter={[32, 32]} justify="center">
          <Col xl={24} flex="0 1 1600px">
            <ActiveFilterBar
              activeFilters={mapToActiveFilter(selectedCollapseFilter)}
              onRemove={onRemoveActiveFilter}
              onRemoveAll={deleteAllFilters}
            />
          </Col>
        </Row>
        <div className="management-dashboard__charts">
          <Row gutter={[32, 32]} justify="center">
            <Col xl={12} flex="0 1 800px">
              <RequestStatusStatisticsChart
                isLoading={isLoading()}
                chartSettings={getRequestStatusChartSettings()}
              />
            </Col>
            <Col xl={12} flex="0 1 800px">
              <TotalFteChart
                data={dashboardStatistics?.totalFte}
                isLoading={isLoading()}
              />
            </Col>
          </Row>
          <Row gutter={[32, 32]} justify="center">
            <Col xl={12} flex="0 1 800px">
              <RequestCountryStatisticsChart
                requestCountryStatisticsData={
                  dashboardStatistics?.countryStatistics
                }
                isLoading={isLoading()}
              />
            </Col>
            <Col xl={12} flex="0 1 800px">
              <Row gutter={[32, 32]} wrap={false}>
                <Col span={14} className="ant-col--request-replies">
                  <RequestRepliedChart
                    requested={
                      dashboardStatistics?.requestStatusStatistics?.requested
                    }
                    replied={
                      dashboardStatistics?.requestStatusStatistics?.replied
                    }
                    isLoading={isLoading()}
                  />
                </Col>
                <Col span={10} className="ant-col--simple-custom-cards">
                  <SimpleCustomCard
                    data={
                      dashboardStatistics?.requestStatusStatistics
                        ?.requiredAction
                    }
                    title="Require action"
                    type="require-action"
                    isLoading={isLoading()}
                  />
                  <SimpleCustomCard
                    data={
                      dashboardStatistics?.requestStatusStatistics?.declinedTag
                    }
                    title={
                      <div className="management-dashboard__declined-requests-label">
                        Declined requests
                        <p>(by Project Lead)</p>
                      </div>
                    }
                    type="decline"
                    isLoading={isLoading()}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </div>
      </div>

      <Layout.Sider
        className="management-dashboard__filter"
        collapsed={false}
        width="300px"
        role="side-navigation"
      >
        <FilterSelection
          className="management-dashboard__month-range-select"
          headline="Time Range"
          options={getTimeRangeOptions()}
          onFilter={onFilterTimeRange}
          defaultValue={timeRangeFilter}
        />
        <FilterCollapse
          filterPanels={filterCollapseData}
          onFilter={onFilterCollapse}
          disabled={isLoading()}
        />
      </Layout.Sider>
    </Layout>
  );
};

export default DashBoardPage;
